import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { PersonAdapter } from 'src/model/person.model';
import { IAPIProfile, Profile } from 'src/model/profile.model';

interface MergeProfileOptions {
  sourceId: number,
  targetId: number,
  mergeReserve: boolean
}

interface ProfileIdOptions {
  profileIds?: number[],
  sharedProfileIds?: number[]
}

@Injectable({
  providedIn: 'root'
})
export class ProfileAdapter extends PersonAdapter {

  adapt(item: any): Profile {
    return new Profile(
      item.id,
      item.occupation,
      item.salary,
      item.salaryDay,
      item.remark,
      this.processCustomerStatus(item.status),
      new Date(item.dateCreated),
      item.customerId,
      item.customerCode,
      item.reserve ?? 0,
      item.badDebt,
      item.name,
      item.sharedProfileId,
      item.companyId,
      item.companyCode,
      item.icNumber
    )
  }

  toApiModel(profile: Profile): IAPIProfile {
    return {
      ...(profile.ID && { id: profile.ID }),
      occupation: profile.Occupation,
      salary: profile.Salary,
      salaryDay: profile.SalaryDay,
      ...(profile.ICNumber && { icNumber: profile.ICNumber }),
      ...(profile.Remark && { remark: profile.Remark }),
      ...(profile.Status && { status: profile.Status.map(s => s.StatusCode) }),
      ...(profile.DateCreated && { dateCreated: profile.DateCreated.toISOString() }),
      customerId: profile.CustomerID,
      customerCode: profile.CustomerCode,
      ...(profile.Reserve && { reserve: profile.Reserve }),
      ...(profile.BadDebt && { badDebt: profile.BadDebt }),
      ...(profile.CustomerCode && { customerName: profile.CustomerCode }),
      ...(profile.SharedProfileId && { sharedProfileId: profile.SharedProfileId }),
      ...(profile.CompanyId && { companyId: profile.CompanyId }),
      ...(profile.CompanyCode && { companyCode: profile.CompanyCode }),
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(ProfileAdapter);

  getCheckCustomerExisted(ic: string, code: string): Promise<Profile> {
    return new Promise<Profile>((resolve, reject) => {
      this.api.get<Profile>(this.constructApi.getCheckCustomerExistence(ic, code)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            if (err.status !== 404) {
              this.api.handleError(err);
              console.error('Failed to check customer existence: ', err);
            }
            reject(err);
          }
        }
      )
    });
  }

  getAllProfiles(profileIds?: number[]): Promise<Profile[]> {
    return new Promise<Profile[]>((resolve, reject) => {
      this.api.get<Profile[]>(this.constructApi.getAllProfiles(profileIds)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get all profiles: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getAllCustomersProfiles(): Promise<Profile[]> {
    return new Promise<Profile[]>((resolve, reject) => {
      this.api.get<Profile[]>(this.constructApi.getAllCustomersProfiles()).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get all customers profiles: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getCustomersByProfileIds(option: ProfileIdOptions): Promise<Profile[]> {
    return new Promise<Profile[]>((resolve, reject) => {
      this.api.get<Profile[]>(this.constructApi.getCustomerByProfileIds(option?.profileIds, option?.sharedProfileIds)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: (response) => { resolve(response) },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to get customers by profile ids: ', err);
          reject(err);
        }
      })
    })
  }

  getCompanyProfileSearch(compId: number, name?: string, ic?: string, custCode?: string): Promise<Profile[]> {
    return new Promise<Profile[]>((resolve, reject) => {
      this.api.get<Profile[]>(this.constructApi.getCompanyProfileSearch(compId, name, ic, custCode)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to search company profiles: ', err);
            reject(err);
          }
        }
      )
    })
  }

  getProfile(id: number): Promise<Profile> {
    return new Promise<Profile>((resolve, reject) => {
      this.api.get<Profile>(this.constructApi.getProfile(id)).pipe(
        map(data => this.adapter.adapt(data))
      ).subscribe(
        {
          next: response => {
            resolve(response);
          },
          error: err => {
            this.api.handleError(err);
            console.error('Failed to get all profile: ', err);
            reject(err);
          }
        }
      )
    })
  }

  updateProfile(profile: Profile): Promise<Profile> {
    return new Promise<Profile>((resolve, reject) => {
      this.api.put<Profile>(this.constructApi.getUpdateProfile(profile.ID), this.adapter.toApiModel(profile)).subscribe({
        next: response => {
          resolve(this.adapter.adapt(response));
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to update profile: ', err);
          reject(err);
        }
      })
    })
  }

  addProfile(profile: Profile): Promise<Profile> {
    return new Promise<Profile>((resolve, reject) => {
      this.api.post<Profile>(this.constructApi.getAddProfile(), this.adapter.toApiModel(profile)).subscribe({
        next: response => {
          resolve(this.adapter.adapt(response));
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to add profile: ', err);
          reject(err);
        }
      })
    })
  }

  moveProfile(profileID: number, customerID: number, isForced: boolean) {
    const data = { 'customerId': customerID }
    const url = isForced ? this.constructApi.getForceMoveProfile(profileID) : this.constructApi.getMoveProfile(profileID);
    return new Promise((resolve, reject) => {
      this.api.put(url, data).subscribe({
        next: response => {
          resolve(true);
        },
        error: err => {
          this.api.handleError(err);
          console.error('Failed to move profile: ', err);
          reject(err);
        }
      })
    })
  }

  mergeProfiles(o: MergeProfileOptions) {
    return new Promise((resolve, reject) => {
      this.api.post(this.constructApi.getMergeProfiles(), o).subscribe({
        next: response => {
          resolve(true);
        },
        error: err => {
          reject(err);
          this.api.handleError(err);
          console.error('Failed to merge profile: ', err);
        }
      })
    })
  }
}
